import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  LoaderOverlay,
} from '@/shared/ui';
import { useDealFunnel, useMyDashboard } from '@entities/Deal';

const KPI_KEYS = [
  'submitted',
  'in_review',
  'accepted',
  'declined',
  'conversion',
] as const;
const KPI_LABELS = {
  accepted: 'pages.agent.analytics.accepted',
  conversion: 'pages.agent.analytics.conversion',
  declined: 'pages.agent.analytics.declined',
  in_review: 'pages.agent.analytics.in_review',
  submitted: 'pages.agent.analytics.submitted',
} as const satisfies Record<(typeof KPI_KEYS)[number], string>;

export const AgentAnalytics: FC = () => {
  const { t } = useTranslation();
  const funnel = useDealFunnel();
  const dashboard = useMyDashboard();

  if (funnel.isLoading || dashboard.isLoading) return <LoaderOverlay show />;
  if (funnel.isError || !funnel.data)
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.deals.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {KPI_KEYS.map((key) => (
        <div key={key} className="rounded-2xl border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">{t(KPI_LABELS[key])}</p>
          <p className="mt-2 text-3xl font-semibold">{funnel.data[key]}</p>
          {key === 'submitted' && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('pages.agent.analytics.assigned', {
                count: dashboard.data?.assigned_to_me ?? 0,
              })}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

AgentAnalytics.displayName = 'AgentAnalytics';
